import { GARMCategories } from 'src/hooks/useDashboardCategories';
import { getNumericRiskValue } from 'src/hooks/useDashboardFilters';
import { CampaignItemProps, CampaignRowsView } from 'src/types/campaign';

/** Show view: API keys on each row (campaign processor). */
const SHOW_SCORE_KEY: Record<string, string> = {
  garm1Risk: 'show_scores_1. Adult Sexual Explicit Risk',
  garm7Risk: 'show_scores_7. Obscenity & Profanity Risk',
  garm11Risk:
    'show_scores_11. Debated Sensitive Social Issues (Political issues) Risk',
  garm8Risk:
    'show_scores_8. Illegal Drugs/ Tobacco/e-Cigarettes/Vaping/Alcohol Risk',
  garm2Risk: 'show_scores_2. Arms & Ammunition Risk',
  garm5Risk: 'show_scores_5 & 9. Online Piracy & Spam Risk',
  garm10Risk: 'show_scores_10. Terrorism Risk',
  garm4Risk: 'show_scores_4. Death, Injury or Military Conflict Risk',
  garm3Risk: 'show_scores_3. Crime & Violation of Human Rights Risk',
  garm6Risk: 'show_scores_6. Hate Speech & Acts of Aggression Risk',
  gamblingRisk: 'show_scores_GAMBLING Risk',
  occultRisk: 'show_scores_OCCULT Risk',
  naturalDisasterRisk: 'show_scores_NATURAL DISASTER Risk',
  generalElectionsRisk: 'show_scores_General Election Risk',
};

/** Episode view: API keys on each row. */
const EPISODE_SCORE_KEY: Record<string, string> = {
  garm1Risk: 'episode_scores_1. Adult Sexual Explicit Risk',
  garm7Risk: 'episode_scores_7. Obscenity & Profanity Risk',
  garm11Risk:
    'episode_scores_11. Debated Sensitive Social Issues (Political issues) Risk',
  garm8Risk:
    'episode_scores_8. Illegal Drugs/ Tobacco/e-Cigarettes/Vaping/Alcohol Risk',
  garm2Risk: 'episode_scores_2. Arms & Ammunition Risk',
  garm5Risk: 'episode_scores_5 & 9. Online Piracy & Spam Risk',
  garm10Risk: 'episode_scores_10. Terrorism Risk',
  garm4Risk: 'episode_scores_4. Death, Injury or Military Conflict Risk',
  garm3Risk: 'episode_scores_3. Crime & Violation of Human Rights Risk',
  garm6Risk: 'episode_scores_6. Hate Speech & Acts of Aggression Risk',
  gamblingRisk: 'episode_scores_GAMBLING Risk',
  occultRisk: 'episode_scores_OCCULT Risk',
  naturalDisasterRisk: 'episode_scores_NATURAL DISASTER Risk',
  generalElectionsRisk: 'episode_scores_General Election',
};

function scoreToMedianValue(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  if (typeof raw === 'string') {
    const n = getNumericRiskValue(raw);
    return typeof n === 'number' && !Number.isNaN(n) ? n : null;
  }
  return null;
}

function buildRiskHistoriesFromShowRow(
  row: Record<string, unknown>
): Record<
  string,
  { medianRiskValue: number | null; medianRisk: number | null }
> {
  const current: Record<
    string,
    { medianRiskValue: number | null; medianRisk: number | null }
  > = {};
  for (const cat of GARMCategories[0].entries) {
    const dk = cat.dashboardFilterKey;
    if (!dk) continue;
    const apiKey = SHOW_SCORE_KEY[dk];
    if (!apiKey) continue;
    const raw = row[apiKey];
    const mv = scoreToMedianValue(raw);
    current[dk] = { medianRiskValue: mv, medianRisk: mv };
  }
  return current;
}

function alignmentToCode(raw: unknown): number {
  if (raw === null || raw === undefined) return -1;
  const n = Number(raw);
  if (Number.isNaN(n)) return -1;
  if (n === 1 || n === 0 || n === -2 || n === -1) return n;
  return -1;
}

/**
 * Turn API violation strings into short display labels for show or episode view.
 *
 * API often combines both levels with "|", e.g.
 * "Episode: episode_scores_1. Adult Sexual Explicit Risk: High | Show: show_scores_3. Crime & Violation of Human Rights Risk: High"
 *
 * - episode view: only Episode: / episode_scores_* segments
 * - show view: Show: / show_scores_* segments, plus exclusion-style text (genre/IAB/show exclusions)
 */
export function formatViolationReason(
  raw: string,
  level: 'show' | 'episode'
): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';

  const sections = trimmed
    .split(/\s*\|\s*/)
    .map((s) => s.trim())
    .filter(Boolean);

  const filtered = sections.filter((section) => {
    const isEpisodePrefixed = /^Episode\s*:/i.test(section);
    const isShowPrefixed = /^Show\s*:/i.test(section);
    const hasEpisodeScores = /episode_scores_/i.test(section);
    const hasShowScores = /show_scores_/i.test(section);

    if (level === 'episode') {
      return isEpisodePrefixed || (hasEpisodeScores && !isShowPrefixed);
    }

    // Show level: show risk categories + exclusions; never episode-only risk text
    if (
      isEpisodePrefixed ||
      (hasEpisodeScores && !hasShowScores && !isShowPrefixed)
    ) {
      return false;
    }
    return true;
  });

  if (filtered.length === 0) return '';

  const labels: string[] = [];
  for (const section of filtered) {
    const re = /\.\s*(.+?)\s*Risk\s*:/gi;
    let match: RegExpExecArray | null;
    let foundRiskLabel = false;
    while ((match = re.exec(section)) !== null) {
      const label = match[1].trim();
      if (label) {
        labels.push(label);
        foundRiskLabel = true;
      }
    }
    if (!foundRiskLabel) {
      const cleaned = section.replace(/^(?:Show|Episode)\s*:\s*/i, '').trim();
      if (cleaned) labels.push(cleaned);
    }
  }

  return labels.join(', ');
}

function violationReasonFromRow(row: Record<string, unknown>): string {
  const raw = row.violation_reason ?? row['Violation Reason'];
  if (raw == null) return '';
  const s = String(raw).trim();
  return s;
}

const placeholderDate = new Date(0);

function baseCampaignItem(
  overrides: Partial<CampaignItemProps>
): CampaignItemProps {
  return {
    showIdentifier: '',
    episodeIdentifier: '',
    viewDate: placeholderDate,
    impressionCount: 0,
    bidsCount: 0,
    advertiserCost: 0,
    winRate: 0,
    advertiserCPM: '',
    conversionEvent1: '',
    conversionEvent2: '',
    showId: '',
    episodeId: '',
    id: '',
    createdAt: placeholderDate,
    updatedAt: placeholderDate,
    episode: {},
    show: {},
    ...overrides,
  };
}

/**
 * Unwrap campaign-rows API payload: shows may be a raw array; episodes use `{ data, total, ... }`.
 */
export function parseCampaignRowsPayload(data: unknown): {
  rows: unknown[];
  total: number;
} {
  if (Array.isArray(data)) {
    return { rows: data, total: data.length };
  }
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    const nested = o.data ?? o.items ?? o.rows;
    const rows = Array.isArray(nested) ? nested : [];
    const total = Number(o.total ?? o.count ?? rows.length) || rows.length;
    return { rows, total };
  }
  return { rows: [], total: 0 };
}

export function mapShowApiRowToCampaignItem(
  row: Record<string, unknown>,
  index: number
): CampaignItemProps {
  const title = String(row.show_title ?? '');
  const impressions = Number(row.impressions ?? 0) || 0;
  const riskCurrent = buildRiskHistoriesFromShowRow(row);

  const show = {
    show: title,
    showId: title,
    rating: undefined as number | undefined,
    riskHistories: {
      current: riskCurrent,
      prev: undefined as Record<string, unknown> | undefined,
    },
  };

  return baseCampaignItem({
    id: `show-${title}-${index}`,
    showIdentifier: title,
    episodeIdentifier: '',
    showId: title,
    episodeId: '',
    impressionCount: impressions,
    alignmentCode: alignmentToCode(row.show_alignment),
    violationReason: violationReasonFromRow(row),
    show,
    episode: {},
  });
}

function episodeRowAlignmentRaw(row: Record<string, unknown>): unknown {
  return (
    row['Episode Alignment'] ??
    row['Data Alignment'] ??
    row['episode_alignment'] ??
    row['data_alignment']
  );
}

export function mapEpisodeApiRowToCampaignItem(
  row: Record<string, unknown>,
  index: number
): CampaignItemProps {
  const episode: Record<string, unknown> = {
    'Episode Title': (row.matched_episodeTitle || row['Episode']) ?? '',
    Show: row.show_scores_showTitle ?? '',
  };

  for (const cat of GARMCategories[0].entries) {
    const jsonKey = cat.jsonKeyRisk;
    if (!jsonKey) continue;
    const dk = cat.dashboardFilterKey;
    if (!dk) continue;
    const apiKey = EPISODE_SCORE_KEY[dk];
    if (!apiKey) continue;
    episode[jsonKey] = row[apiKey] ?? null;
  }
  // CampaignItemEpisodeRow dataMap uses `GARM 5&9 Risk` (see dataMap in that file)
  if (episode['GARM 5 & 9 Risk'] !== undefined) {
    episode['GARM 5&9 Risk'] = episode['GARM 5 & 9 Risk'];
  }

  const guid = row.matched_episode_scores_Guid;
  const impressions = Number(row.Impressions ?? row.impressions ?? 0) || 0;

  return baseCampaignItem({
    id: guid != null && String(guid) ? String(guid) : `episode-${index}`,
    showIdentifier: String(row.show_scores_showTitle ?? ''),
    episodeIdentifier: String(
      (row.matched_episodeTitle || row['Episode']) ?? ''
    ),
    showId: String(row.show_scores_showTitle ?? ''),
    episodeId: guid != null ? String(guid) : '',
    impressionCount: impressions,
    alignmentCode: alignmentToCode(episodeRowAlignmentRaw(row)),
    violationReason: violationReasonFromRow(row),
    episode,
    show: {},
  });
}

export function mapCampaignRowsToItems(
  view: CampaignRowsView,
  rows: unknown[]
): CampaignItemProps[] {
  return rows.map((raw, index) => {
    const row = raw as Record<string, unknown>;
    return view === 'episodes'
      ? mapEpisodeApiRowToCampaignItem(row, index)
      : mapShowApiRowToCampaignItem(row, index);
  });
}

/**
 * Preferred CSV column order for episode exports — matches GET /campaign-rows
 * episodes payload field names and typical backend ordering.
 */
export const EPISODE_BACKEND_ROW_COLUMN_PRIORITY: readonly string[] = [
  '% of Total Campaign',
  'Content Title',
  'Data Alignment',
  'Episode',
  'Episode Alignment',
  'Impressions',
  'Violation Reason',
  'episode_scores_1. Adult Sexual Explicit Risk',
  'episode_scores_10. Terrorism Risk',
  'episode_scores_11. Debated Sensitive Social Issues (Political issues) Risk',
  'episode_scores_2. Arms & Ammunition Risk',
  'episode_scores_3. Crime & Violation of Human Rights Risk',
  'episode_scores_4. Death, Injury or Military Conflict Risk',
  'episode_scores_5 & 9. Online Piracy & Spam Risk',
  'episode_scores_6. Hate Speech & Acts of Aggression Risk',
  'episode_scores_7. Obscenity & Profanity Risk',
  'episode_scores_8. Illegal Drugs/ Tobacco/e-Cigarettes/Vaping/Alcohol Risk',
  'episode_scores_GAMBLING Risk',
  'episode_scores_General Election',
  'episode_scores_NATURAL DISASTER Risk',
  'episode_scores_OCCULT Risk',
  'matched_episodeTitle',
  'matched_episode_scores_Guid',
  'show_scores_showTitle',
];

export function buildEpisodeCsvExportHeaders(
  rows: Record<string, unknown>[]
): string[] {
  const present = new Set<string>();
  rows.forEach((r) => Object.keys(r).forEach((k) => present.add(k)));
  const headers: string[] = [];
  for (const k of EPISODE_BACKEND_ROW_COLUMN_PRIORITY) {
    if (present.has(k)) headers.push(k);
  }
  const extras = Array.from(present)
    .filter((k) => !headers.includes(k))
    .sort((a, b) => a.localeCompare(b));
  return [...headers, ...extras];
}

function csvEscapeCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  const escaped = s.replace(/"/g, '""');
  if (/[",\n\r]/.test(s)) return `"${escaped}"`;
  return escaped;
}

/** One CSV line per row; values aligned to `headers` (RFC-style quoting). */
export function episodeBackendRowsToCsvBody(
  rows: Record<string, unknown>[],
  headers: string[]
): string {
  return rows
    .map((row) => headers.map((h) => csvEscapeCell(row[h])).join(','))
    .join('\n');
}
